import { type KeyboardEvent, useId, useState } from "react";
import { type AntibiogramEntry, antibioticAnswers, type CalculatedValue, calculatedHint, computeCalculated, type CultureIsolate, type CultureResult, type CultureShape, cultureShape, DEFAULT_FORMULA_DECIMALS, formulaCodeKey, formulaNumber, type Gram, isAntibioticAnalyte, isCalculated, type OrderItemResults, type ResultColour, type ResultValueInput, richTextToPlain, sanitizeRichText } from "@probetalab/contracts";
import { fmtNumber } from "../lib/format.js";
import { COLOUR_LABEL, DIRECTION_ARROW, DIRECTION_LABEL, previewColour, valueDirection } from "../lib/status.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";
import { CultureCapture, DocumentRow, StudyNote } from "./CaptureDocuments.js";
import { StudyAttachments } from "./CaptureAttachments.js";
import { rememberAttachment } from "../lib/attachments.js";
import { RefText } from "./RefText.js";
import { Icon } from "./icons.js";

/**
 * The analyte rows of one study, shared by the single-study capture screen and the whole-order
 * screen (Fernanda, 2026-11-02 19:18: "que al seleccionar el paciente se puedan editar todos los
 * estudios seguidos"). Every input carries `data-capture-input`, so Enter/Tab moves to the next
 * enabled input in DOM order within the nearest `data-capture-scope`, across studies.
 */

/**
 * Typed text per analyte id, plus three kinds of keys of ADR 0019:
 *   `culture:<document analyte id>`  the culture's outcome as canonical JSON ("" = not chosen yet)
 *   `pick:<antibiotic id>`           "1" while an antibiotic is chosen but not answered yet
 *   NOTE_KEY                          the study's note (sanitised HTML)
 *   ATTACH_KEY                        the study's attached documents, ids in print order joined by "," (ADR 0030)
 *   LAB_KEY                           «Laboratorio que realizó el estudio» (ADR 0030)
 * A `document` analyte's own key holds its formatted text (sanitised HTML).
 */
export type Draft = Record<string, string>;

type Analyte = OrderItemResults["analytes"][number];

export const NOTE_KEY = "__note";
export const ATTACH_KEY = "__attachments";
export const LAB_KEY = "__performed_by_lab";

/** The attachment ids a draft holds, in print order. */
export function attachmentIds(draft: Draft): string[] {
  return (draft[ATTACH_KEY] ?? "").split(",").filter(Boolean);
}

/** The study's first document row that is not a culture's: where the attachments are shown (ADR 0030). */
export function plainDocumentAnalyte(r: Pick<OrderItemResults, "analytes">): Analyte | null {
  const culture = cultureOf(r);
  return r.analytes.find((a) => a.kind === "document" && a.analyte_id !== culture?.document.analyte_id) ?? null;
}
export const cultureKey = (documentId: string) => `culture:${documentId}`;
export const pickKey = (antibioticId: string) => `pick:${antibioticId}`;

export function isLocked(r: Pick<OrderItemResults, "state">): boolean {
  return r.state === "validated" || r.state === "issued" || r.state === "cancelled";
}

/** The study's culture structure (ADR 0019), or null. */
export function cultureOf(r: Pick<OrderItemResults, "analytes">): CultureShape<Analyte> | null {
  return cultureShape(r.analytes);
}

/**
 * The analytes that must have a value for the study to be complete: every value row, except a
 * culture's antibiotics, which are chosen per order.
 */
export function capturableAnalytes(r: OrderItemResults): Analyte[] {
  const culture = cultureOf(r);
  const optional = new Set(culture?.antibiotics.map((a) => a.analyte_id) ?? []);
  return r.analytes.filter((a) => a.kind !== "subtitle" && !optional.has(a.analyte_id) && !isCalculatedAnalyte(a));
}

/** Every analyte that takes a typed value (antibiotics included; calculated ones are computed, ADR 0023). */
function valueAnalytes(r: OrderItemResults): Analyte[] {
  return r.analytes.filter((a) => a.kind !== "subtitle" && !isCalculatedAnalyte(a));
}

/**
 * One canonical string per culture draft, so "unchanged" compares as text. An isolate without a
 * Gram serialises exactly as before ADR 0026, so a culture captured the old way is not dirty.
 */
export function cultureToDraft(c: CultureDraft | null | undefined): string {
  if (!c) return "";
  return JSON.stringify({
    outcome: c.outcome,
    isolates: c.isolates.map((i) => ({
      organism: i.organism,
      count: i.count ?? "",
      ...(i.gram ? { gram: i.gram } : {}),
      ...(i.antibiogram?.length ? { antibiogram: i.antibiogram.map((e) => ({ antibiotic: e.antibiotic, result: e.result ?? null })) } : {}),
    })),
  });
}

export interface IsolateDraft {
  organism: string;
  count: string | null;
  /** ADR 0026: unset = not classified (the study's own antibiogram applies, ADR 0019). */
  gram?: Gram;
  /** The isolate's own antibiogram, in print order; entries without a result are chosen, not answered. */
  antibiogram?: AntibiogramEntry[];
}

export interface CultureDraft {
  outcome: "negative" | "positive";
  isolates: IsolateDraft[];
}

/** An isolate whose Gram carries a panel antibiogram. */
export function hasPanelGram(i: Pick<IsolateDraft, "gram">): i is { gram: "positive" | "negative" } {
  return i.gram === "positive" || i.gram === "negative";
}

export function cultureFromDraft(s: string | undefined): CultureDraft | null {
  if (!s) return null;
  try {
    const v = JSON.parse(s) as CultureDraft;
    return v.outcome === "negative" || v.outcome === "positive" ? { outcome: v.outcome, isolates: Array.isArray(v.isolates) ? v.isolates : [] } : null;
  } catch {
    return null;
  }
}

/** The culture as the server takes it: trimmed, empty organisms dropped; null while incomplete. */
export function cultureResult(d: CultureDraft | null): CultureResult | null {
  if (!d) return null;
  if (d.outcome === "negative") return { outcome: "negative", isolates: [] };
  const isolates = d.isolates
    .map((i): CultureIsolate => {
      const out: CultureIsolate = { organism: i.organism.trim(), count: (i.count ?? "").trim().replace(/^m[aá]s de$/i, "") || null };
      if (i.gram) out.gram = i.gram;
      if (hasPanelGram(i) && i.antibiogram?.length) out.antibiogram = i.antibiogram.map((e) => ({ antibiotic: e.antibiotic, result: e.result ?? null }));
      return out;
    })
    .filter((i) => i.organism !== "");
  return isolates.length ? { outcome: "positive", isolates } : null;
}

export function isCalculatedAnalyte(a: Analyte): boolean {
  return isCalculated({ kind: a.kind, formula: a.formula ?? null });
}

/**
 * Calculated analytes computed live from what is typed (ADR 0023), with the same evaluator the
 * server runs on save — so what the chemist sees is what gets stored. Keyed by analyte id.
 */
export function liveCalculated(r: OrderItemResults, draft: Draft): Map<string, CalculatedValue> {
  if (!r.analytes.some(isCalculatedAnalyte)) return new Map();
  const byCode = new Map(r.analytes.map((a) => [formulaCodeKey(a.code), a]));
  const typed = (code: string): number | null => formulaNumber(draft[byCode.get(code)!.analyte_id]);
  const analytes = r.analytes.map((a) => ({ id: a.analyte_id, code: a.code, kind: a.kind, decimals: a.decimals, formula: a.formula ?? null }));
  return computeCalculated(analytes, typed, r.formula_patient ?? { edad_anios: null, sexo: null });
}

/** The stored values as the inputs show them. */
export function draftFrom(r: OrderItemResults): Draft {
  const d: Draft = {};
  const culture = cultureOf(r);
  for (const v of r.values) {
    const a = r.analytes.find((x) => x.analyte_id === v.analyte_id);
    if (a?.kind === "document") {
      d[v.analyte_id] = sanitizeRichText(v.value_text);
      if (culture?.document.analyte_id === v.analyte_id) d[cultureKey(v.analyte_id)] = cultureToDraft(v.culture);
    } else d[v.analyte_id] = v.value_numeric !== null ? String(v.value_numeric) : (v.value_text ?? "");
  }
  // The laboratory's report template ("documento precargado", pilot report 2026-11-03 09:03):
  // a document with nothing saved starts with it, as in the previous system.
  if (!isLocked(r)) {
    for (const [id, html] of Object.entries(r.document_templates ?? {})) {
      if (d[id] === undefined && r.analytes.some((a) => a.analyte_id === id && a.kind === "document")) d[id] = sanitizeRichText(html);
    }
  }
  d[NOTE_KEY] = sanitizeRichText(r.note ?? "");
  for (const a of r.attachments ?? []) rememberAttachment(a);
  d[ATTACH_KEY] = (r.attachments ?? []).map((a) => a.id).join(",");
  d[LAB_KEY] = r.performed_by_lab ?? "";
  return d;
}

/**
 * A document that still holds only its untouched template and has nothing saved. Outside a
 * culture that is not a result: it does not count as filled and is not sent.
 */
export function isUntouchedTemplate(r: OrderItemResults, analyteId: string, html: string | undefined): boolean {
  const tpl = r.document_templates?.[analyteId];
  if (!tpl || r.values.some((v) => v.analyte_id === analyteId)) return false;
  return sanitizeRichText(html ?? "") === sanitizeRichText(tpl);
}

/** True when the analyte's input counts as filled. */
function isFilled(r: OrderItemResults, a: Analyte, draft: Draft): boolean {
  if (a.kind === "document") {
    const culture = cultureOf(r);
    if (culture?.document.analyte_id === a.analyte_id) return cultureResult(cultureFromDraft(draft[cultureKey(a.analyte_id)])) !== null;
    // ADR 0030: the outside laboratory's PDF is the report; an attached document completes the row.
    if (attachmentIds(draft).length > 0) return true;
    if (isUntouchedTemplate(r, a.analyte_id, draft[a.analyte_id])) return false;
    return richTextToPlain(draft[a.analyte_id]) !== "" || r.values.some((v) => v.analyte_id === a.analyte_id && v.document_artifact_id !== null);
  }
  return (draft[a.analyte_id] ?? "").trim() !== "";
}

export function filledCount(r: OrderItemResults, draft: Draft): number {
  return capturableAnalytes(r).filter((a) => isFilled(r, a, draft)).length;
}

/** The keys an input writes to: every value analyte, the culture's outcome, the note. */
function draftKeys(r: OrderItemResults): string[] {
  const culture = cultureOf(r);
  return [...valueAnalytes(r).map((a) => a.analyte_id), ...(culture ? [cultureKey(culture.document.analyte_id)] : []), NOTE_KEY, ATTACH_KEY, LAB_KEY];
}

/** True when some input differs from what the server holds. */
export function isDirty(r: OrderItemResults, draft: Draft, baseline: Draft): boolean {
  const culture = cultureOf(r);
  const negative = culture ? cultureFromDraft(draft[cultureKey(culture.document.analyte_id)])?.outcome === "negative" : false;
  const antibiotics = new Set(culture?.antibiotics.map((a) => a.analyte_id) ?? []);
  return draftKeys(r).some((k) => {
    // A negative culture withdraws every antibiotic on save: what is typed there no longer counts.
    const mine = negative && antibiotics.has(k) ? "" : (draft[k] ?? "").trim();
    return mine !== (baseline[k] ?? "").trim();
  });
}

/**
 * The `results.capture` values: every filled input, numeric ones parsed with a decimal comma
 * accepted; a culture's document with its outcome; and `clear` for an antibiotic the server holds
 * that is no longer chosen (or every antibiotic, once the culture is negative).
 */
export function valuesFromDraft(r: OrderItemResults, draft: Draft): ResultValueInput[] {
  const culture = cultureOf(r);
  const outcome = culture ? cultureResult(cultureFromDraft(draft[cultureKey(culture.document.analyte_id)])) : null;
  const antibiotics = new Set(culture?.antibiotics.map((a) => a.analyte_id) ?? []);
  const stored = new Set(r.values.map((v) => v.analyte_id));
  const out: ResultValueInput[] = [];
  const base = { value_numeric: null, value_text: null, document_artifact_id: null, culture: null, clear: false };
  for (const a of valueAnalytes(r)) {
    const raw = (draft[a.analyte_id] ?? "").trim();
    if (antibiotics.has(a.analyte_id)) {
      const keep = outcome?.outcome !== "negative" && raw !== "";
      if (keep) out.push({ ...base, analyte_id: a.analyte_id, value_text: raw });
      else if (stored.has(a.analyte_id)) out.push({ ...base, analyte_id: a.analyte_id, clear: true });
      continue;
    }
    if (a.kind === "document") {
      const isCultureDoc = culture?.document.analyte_id === a.analyte_id;
      const text = !isCultureDoc && isUntouchedTemplate(r, a.analyte_id, raw) ? null : sanitizeRichText(raw) || null;
      const artifact = r.values.find((v) => v.analyte_id === a.analyte_id)?.document_artifact_id ?? null;
      const c = culture?.document.analyte_id === a.analyte_id ? outcome : null;
      if (text || artifact || c) out.push({ ...base, analyte_id: a.analyte_id, value_text: text, document_artifact_id: artifact, culture: c });
      continue;
    }
    if (raw === "") continue;
    if (a.kind === "numeric") {
      const n = Number(raw.replace(",", "."));
      out.push({ ...base, analyte_id: a.analyte_id, value_numeric: Number.isFinite(n) ? n : null, value_text: Number.isFinite(n) ? null : raw });
    } else out.push({ ...base, analyte_id: a.analyte_id, value_text: raw });
  }
  return out;
}

/** The note to send: undefined when it did not change. */
export function noteFromDraft(r: OrderItemResults, draft: Draft): string | undefined {
  const note = sanitizeRichText(draft[NOTE_KEY] ?? "");
  return note === sanitizeRichText(r.note ?? "") ? undefined : note;
}

/** The attachments and the performing laboratory to send (ADR 0030): only what changed. */
export function annexFromDraft(r: OrderItemResults, draft: Draft): { attachments?: string[]; performed_by_lab?: string } {
  const ids = attachmentIds(draft);
  const saved = (r.attachments ?? []).map((a) => a.id);
  const lab = (draft[LAB_KEY] ?? "").trim().replace(/\s+/g, " ");
  return {
    ...(ids.join(",") !== saved.join(",") ? { attachments: ids } : {}),
    ...(lab !== (r.performed_by_lab ?? "") ? { performed_by_lab: lab } : {}),
  };
}

export interface CapturePayloadShape {
  order_item_id: string;
  values: ResultValueInput[];
  note?: string;
  attachments?: string[];
  performed_by_lab?: string;
  mark_captured: boolean;
}

/** The whole `results.capture` payload of one study (values, note, attachments); null when there is nothing to send. */
export function capturePayload(r: OrderItemResults, draft: Draft, markCaptured = true): CapturePayloadShape | null {
  const values = valuesFromDraft(r, draft);
  const note = noteFromDraft(r, draft);
  const annex = annexFromDraft(r, draft);
  if (!values.length && note === undefined && annex.attachments === undefined && annex.performed_by_lab === undefined) return null;
  return { order_item_id: r.order_item_id, values, ...(note !== undefined ? { note } : {}), ...annex, mark_captured: markCaptured };
}

const INPUT_SELECTOR = "[data-capture-input]";

/** Focus the next enabled capture input after `from`; false when it was the last one. */
export function focusNextCaptureInput(from: HTMLElement): boolean {
  const scope: ParentNode = from.closest("[data-capture-scope]") ?? from.ownerDocument;
  const all = [...scope.querySelectorAll<HTMLInputElement | HTMLSelectElement>(INPUT_SELECTOR)].filter((el) => !el.disabled);
  const next = all[all.indexOf(from as HTMLInputElement) + 1];
  if (!next) return false;
  next.focus();
  if (next instanceof HTMLInputElement) next.select();
  return true;
}

/** Focus the first enabled capture input in `scope`. */
export function focusFirstCaptureInput(scope: ParentNode = document): void {
  const el = [...scope.querySelectorAll<HTMLInputElement | HTMLSelectElement>(INPUT_SELECTOR)].find((e) => !e.disabled);
  el?.focus();
}

const isPlainTab = (e: KeyboardEvent) => e.key === "Tab" && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey;

function onCaptureKey(e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) {
  if (e.key === "Enter") {
    e.preventDefault();
    focusNextCaptureInput(e.currentTarget);
  } else if (isPlainTab(e)) {
    // Tab skips the buttons between studies; past the last input it falls through to the page.
    if (focusNextCaptureInput(e.currentTarget)) e.preventDefault();
  }
}

/** Case- and accent-insensitive form for matching typed text against the options. */
export function foldText(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase();
}

/** The analyte's allowed answers, in the order stored; empty when it has none. */
export function analyteOptions(a: Analyte): string[] {
  if (a.kind === "subtitle" || a.kind === "document") return [];
  // An antibiotic always answers from a list, its own or the fixed one (ADR 0026 follow-up).
  if (isAntibioticAnalyte(a)) return antibioticAnswers(a);
  return (a.options ?? []).filter((o) => o.trim() !== "");
}

/**
 * "Llenar normales" (Very, 2026-11-02 19:24): every EMPTY field whose catalogue declares a normal
 * answer gets it. Typed values are never touched. Returns the new draft and the ids it filled.
 *
 * The catalogue declares "normal" two ways, and both count (pilot request R-50, Fernanda
 * 2026-11-04): the first entry of an answer list, and the applied interval's `expected_text` for a
 * qualitative analyte with no list. Only the first was honoured, so six analytes of their EGO
 * (GLUCOSA, CUERPOS CETONICOS, PROTEÍNAS EN ORINA, PIOCITOS, CILINDROS, OTROS) were skipped even
 * though the catalogue holds NEGATIVO / NO SE OBSERVARON for each — the button looked unreliable
 * and the fields were left blank. A numeric analyte is never filled: `expected_text` there is a
 * sentence, not a measurement.
 *
 * The button is the default. A laboratory that turned on `capture_prefill_normals` (R-51, ADR 0038)
 * gets the same fill when the screen opens, through `prefillOnOpen` below; by default nothing is
 * filled until someone asks.
 */
export function fillNormals(r: OrderItemResults, draft: Draft): { draft: Draft; filled: string[] } {
  const next = { ...draft };
  const filled: string[] = [];
  for (const a of normalsAnalytes(r)) {
    if ((next[a.analyte_id] ?? "").trim() !== "") continue;
    const normal = normalAnswerFor(a);
    if (!normal) continue;
    next[a.analyte_id] = normal;
    filled.push(a.analyte_id);
  }
  return { draft: next, filled };
}

/**
 * The laboratory setting `capture_prefill_normals` (R-51, ADR 0038): an editable study opens with
 * `fillNormals` already applied. A locked (validated or cancelled) study, or a laboratory with the
 * setting off, comes back untouched with nothing filled. Stored values are never overwritten
 * (`fillNormals` skips non-empty fields), numerics and a culture's antibiotics stay empty.
 */
export function prefillOnOpen(r: OrderItemResults, draft: Draft, enabled: boolean): { draft: Draft; filled: string[] } {
  if (!enabled || isLocked(r)) return { draft, filled: [] };
  return fillNormals(r, draft);
}

/** The answer «Llenar normales» would put in this field, or null when the catalogue declares none. */
export function normalAnswerFor(a: Analyte): string | null {
  const first = analyteOptions(a)[0];
  if (first) return first;
  if (a.kind === "numeric") return null;
  return a.expected_text?.trim() || null;
}

/** «Llenar normales» never touches a culture's antibiotics: choosing them is the chemist's call (ADR 0019). */
function normalsAnalytes(r: OrderItemResults): Analyte[] {
  const antibiotics = new Set(cultureOf(r)?.antibiotics.map((a) => a.analyte_id) ?? []);
  return r.analytes.filter((a) => !antibiotics.has(a.analyte_id));
}

/** True when some field the catalogue declares a normal answer for is still empty. */
export function hasEmptyOptionFields(r: OrderItemResults, draft: Draft): boolean {
  return normalsAnalytes(r).some((a) => normalAnswerFor(a) !== null && (draft[a.analyte_id] ?? "").trim() === "");
}

/**
 * Answers from a list instead of typing (Very, 2026-11-02 19:24, like Toronja): typing filters the
 * list and marks the first match, ↑/↓ move, Enter or Tab take the marked answer and move on, Escape
 * closes it. Nothing is chosen until someone chooses it: no silent default. A `reference` analyte
 * only accepts a listed answer (the server refuses anything else), so on leaving the field a
 * case/accent variant is snapped to the listed spelling; a `text` analyte keeps free text.
 */
export function OptionCombo({ label, value, options, strict, disabled, prefilled, onChange }: { label: string; value: string; options: string[]; strict: boolean; disabled: boolean; prefilled?: boolean; onChange: (value: string) => void }) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const shown = query ? options.filter((o) => foldText(o).includes(foldText(query))) : options;
  const exact = (v: string) => options.find((o) => foldText(o) === foldText(v));
  const invalid = strict && value.trim() !== "" && !options.includes(value);

  function close() {
    setOpen(false);
    setQuery(null);
    setActive(-1);
  }
  function pick(o: string) {
    onChange(o);
    close();
  }
  function snap() {
    if (strict && value.trim() !== "") {
      const m = exact(value);
      if (m && m !== value) onChange(m);
    }
  }
  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setQuery(null);
        setActive(Math.max(0, options.indexOf(value)));
        return;
      }
      const n = shown.length;
      if (!n) return;
      setActive((i) => (e.key === "ArrowDown" ? Math.min(n - 1, i + 1) : Math.max(0, i - 1)));
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        close();
      }
    } else if (e.key === "Enter" || isPlainTab(e)) {
      const chosen = open && active >= 0 ? shown[active] : undefined;
      if (chosen !== undefined) pick(chosen);
      else {
        snap();
        close();
      }
      onCaptureKey(e);
    }
  }

  return (
    <div className="combo">
      <input {...NO_AUTOFILL}
        data-capture-input=""
        role="combobox"
        aria-label={label}
        aria-autocomplete="list"
        aria-expanded={open && shown.length > 0}
        aria-controls={listId}
        aria-activedescendant={open && active >= 0 && shown[active] !== undefined ? `${listId}-${active}` : undefined}
        aria-invalid={invalid || undefined}
        className={`input${prefilled ? " prefilled" : ""}`}
        autoComplete="off"
        value={value}
        disabled={disabled}
        placeholder="Elegir o escribir…"
        onChange={(e) => {
          const v = e.target.value;
          onChange(v);
          setQuery(v);
          setOpen(true);
          const hasMatch = v.trim() !== "" && options.some((o) => foldText(o).includes(foldText(v)));
          setActive(hasMatch ? 0 : -1);
        }}
        onClick={() => {
          if (open) close();
          else {
            setOpen(true);
            setQuery(null);
            setActive(options.indexOf(value));
          }
        }}
        onBlur={() => {
          snap();
          close();
        }}
        onKeyDown={onKey}
      />
      {open && shown.length > 0 && (
        <ul className="combo-list" role="listbox" id={listId} aria-label={`${label}: respuestas`}>
          {shown.map((o, i) => (
            <li
              key={o}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={o === value}
              className={i === active ? "active" : undefined}
              // mousedown, not click: the input must not blur (and close the list) before the pick lands.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(o);
              }}
            >
              {o}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CaptureHead() {
  return (
    <div className="capture-head">
      <span>Parámetro</span>
      <span style={{ textAlign: "center" }}>Resultado</span>
      <span>Unidad</span>
      <span>Referencia</span>
      <span>Anterior</span>
    </div>
  );
}

/** Rows of one study: input, unit, interval with the live colour dot, previous value with its delta. */
export function CaptureRows({
  r,
  draft,
  locked,
  prefilled,
  lowRed = false,
  onChange,
}: {
  r: OrderItemResults;
  draft: Draft;
  locked: boolean;
  /** Lab setting `capture_low_red`: a value below the interval is drawn red, above it orange. */
  lowRed?: boolean;
  /** Analytes filled by "Llenar normales" and not touched since: drawn dashed until edited. */
  prefilled?: ReadonlySet<string>;
  onChange: (analyteId: string, value: string) => void;
}) {
  const prevById = new Map(r.previous.map((p) => [p.analyte_id, p]));
  // A culture is captured as one block (outcome, organisms, report, antibiogram); its document,
  // its antibiotics and the subtitles that only head antibiotics are not rows of their own.
  const culture = cultureOf(r);
  const inCulture = new Set<string>();
  if (culture) {
    inCulture.add(culture.document.analyte_id);
    for (const a of culture.antibiotics) inCulture.add(a.analyte_id);
    r.analytes.forEach((a, i) => {
      if (a.kind !== "subtitle") return;
      const next = r.analytes.slice(i + 1).find((x) => x.kind !== "subtitle");
      if (!next || inCulture.has(next.analyte_id)) inCulture.add(a.analyte_id);
    });
  }
  const calculated = locked ? new Map<string, CalculatedValue>() : liveCalculated(r, draft);
  // ADR 0030: attachments sit under the study's document row, or at the end of any other study.
  const plainDoc = plainDocumentAnalyte(r);
  const attachments = (inDocument: boolean) => <StudyAttachments key="__attachments" r={r} draft={draft} locked={locked} onChange={onChange} collapsible={!inDocument} />;
  return (
    <>
      {culture && <CultureCapture r={r} draft={draft} locked={locked} onChange={onChange} />}
      {r.analytes.map((a) => {
        if (inCulture.has(a.analyte_id)) return null;
        if (a.kind === "document") {
          const row = <DocumentRow key={a.analyte_id} a={a} r={r} draft={draft} locked={locked} onChange={onChange} />;
          return a.analyte_id === plainDoc?.analyte_id ? [row, attachments(true)] : row;
        }
        if (a.kind === "subtitle") {
          return (
            <div key={a.analyte_id} className="analyte-row section">
              <span style={{ gridColumn: "1 / -1" }}>{a.name}</span>
            </div>
          );
        }
        const calc = calculated.get(a.analyte_id);
        const isCalc = isCalculatedAnalyte(a);
        // Locked: the stored value. Open: the live computation (empty when it cannot be computed).
        const val = isCalc && !locked ? (calc?.value == null ? "" : calc.value.toFixed(a.decimals ?? DEFAULT_FORMULA_DECIMALS)) : (draft[a.analyte_id] ?? "");
        const colour: ResultColour = previewColour({ kind: a.kind, value: val, low: a.low, high: a.high, expected: a.expected_text });
        const direction = valueDirection({ value: val, low: a.low, high: a.high });
        const lowTone = lowRed && colour === "yellow" && direction === "low";
        const flag = colour !== "none" && val ? <ResultFlag colour={colour} direction={direction} lowRed={lowTone} /> : null;
        const prev = prevById.get(a.analyte_id);
        const options = analyteOptions(a);
        const isPrefilled = prefilled?.has(a.analyte_id) ?? false;
        return (
          <div key={a.analyte_id} className={`analyte-row ${colour}${lowTone ? " low-red" : ""}`}>
            <div>
              <div className="aname">{a.name}</div>
              <div className="acode">
                {a.code}
                {a.section ? ` · ${a.section}` : ""}
              </div>
            </div>
            <div className="result-cell">
              {isCalc ? (
                <CalculatedCell name={a.name} formula={a.formula!} value={val === "" ? "" : fmtNumber(Number(val), a.decimals ?? DEFAULT_FORMULA_DECIMALS)} hint={!locked && calc ? calculatedHint(calc) : null} />
              ) : options.length > 0 ? (
                <OptionCombo label={a.name} value={val} options={options} strict={a.kind === "reference" || isAntibioticAnalyte(a)} disabled={locked} prefilled={isPrefilled} onChange={(v) => onChange(a.analyte_id, v)} />
              ) : (
                <input {...NO_AUTOFILL}
                  data-capture-input=""
                  className={`input${isPrefilled ? " prefilled" : ""}`}
                  inputMode={a.kind === "numeric" ? "decimal" : "text"}
                  value={val}
                  disabled={locked}
                  placeholder={a.kind === "numeric" ? "0" : (a.expected_text ?? "")}
                  onChange={(e) => onChange(a.analyte_id, e.target.value)}
                  onKeyDown={onCaptureKey}
                  aria-label={a.name}
                />
              )}
              {flag}
            </div>
            <span className="unit">{a.unit ?? ""}</span>
            <span className="interval">
              <RefText text={a.interval_text ?? (a.low !== null || a.high !== null ? `${a.low ?? ""} – ${a.high ?? ""}` : (a.expected_text ?? "—"))} />
            </span>
            <span className="prev">
              {prev ? (
                <>
                  {prev.value_numeric !== null ? fmtNumber(prev.value_numeric, a.decimals) : prev.value_text}
                  {prev.delta !== null && prev.delta !== 0 && (
                    <span style={{ color: prev.delta > 0 ? "var(--crit)" : "var(--blue)" }}>
                      {" "}
                      {prev.delta > 0 ? "▲" : "▼"} {fmtNumber(Math.abs(prev.delta), a.decimals)}
                    </span>
                  )}
                </>
              ) : (
                "—"
              )}
            </span>
          </div>
        );
      })}
      {!plainDoc && attachments(false)}
      <StudyNote r={r} draft={draft} locked={locked} onChange={onChange} />
    </>
  );
}

/** The flag right of the value: the severity dot, plus an arrow and Alto / Bajo when the value is outside a numeric interval. */
export function ResultFlag({ colour, direction, lowRed = false }: { colour: ResultColour; direction: "high" | "low" | null; lowRed?: boolean }) {
  const tone = lowRed ? "red-low" : colour;
  // A critical value (red colour: only possible with real critical limits) is never colour-only:
  // a warning sign and the word, bold, on top of the direction.
  if (colour === "red") {
    return (
      <span className="res result-flag red critical" title={COLOUR_LABEL.red} data-flag="critical">
        <span className="res-dot red" />
        <span className="dir">
          <span aria-hidden="true">⚠</span> Crítico{direction ? ` ${DIRECTION_ARROW[direction]} ${DIRECTION_LABEL[direction]}` : ""}
        </span>
      </span>
    );
  }
  return (
    <span className={`res result-flag ${tone}`} title={direction ? `${DIRECTION_LABEL[direction]} · ${COLOUR_LABEL[colour]}` : COLOUR_LABEL[colour]} data-flag={direction ?? colour}>
      <span className={`res-dot ${tone}`} />
      {direction && (
        <span className="dir">
          <span aria-hidden="true">{DIRECTION_ARROW[direction]}</span> {DIRECTION_LABEL[direction]}
        </span>
      )}
    </span>
  );
}

/**
 * A calculated analyte's result cell (ADR 0023): read-only, out of the Enter/Tab order (no
 * `data-capture-input`), the calculator mark the lab knew from the previous system, and the formula
 * in the tooltip and the accessible name. Without a value it says what is missing instead of 0.
 */
function CalculatedCell({ name, formula, value, hint }: { name: string; formula: string; value: string; hint: string | null }) {
  const hintId = useId();
  return (
    <div className="calc-cell" title={`Se calcula: = ${formula}`}>
      <div className="input-wrap">
        <Icon name="calc" />
        <input
          className="input calc"
          readOnly
          tabIndex={-1}
          value={value}
          placeholder="—"
          aria-label={`${name}, calculado con = ${formula}`}
          aria-describedby={hint ? hintId : undefined}
          data-calculated=""
          {...NO_AUTOFILL}
        />
      </div>
      {hint && (
        <div id={hintId} className="calc-hint">
          {hint}
        </div>
      )}
    </div>
  );
}

/** The colour legend of the action bar; follows the lab setting `capture_low_red`. */
export function ColourLegend({ lowRed = false }: { lowRed?: boolean }) {
  return (
    <>
      <span className="res">
        <span className="res-dot green" /> normal
      </span>{" "}
      ·{" "}
      {lowRed ? (
        <>
          <span className="res">
            <span className="res-dot yellow" /> alto
          </span>{" "}
          ·{" "}
          <span className="res">
            <span className="res-dot red-low" /> bajo
          </span>
        </>
      ) : (
        <span className="res">
          <span className="res-dot yellow" /> alto o bajo
        </span>
      )}
    </>
  );
}
